import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaPaperPlane, FaVolumeMute, FaVolumeUp, FaSpinner } from 'react-icons/fa';
import { useChatBot } from '../../hooks/useChatBot';
import './ChatBot.css'

const DIMENSIONS = {
  mobile: {
    width: 'w-[95vw] max-w-[100%]', // Full width on mobile
    height: 'h-[90vh]', // Take most of the screen height
    bottom: 'bottom-0', // Stick to the bottom
    right: 'right-0', // Stick to the right
    messageHeight: 'h-[75vh]' // Adjust message container height
  },
  tablet: {
    width: 'w-[400px]',
    height: 'h-[80vh]',
    bottom: 'bottom-10',
    right: 'right-0',
    messageHeight: 'h-[75vh]'
  },
  desktop: {
    width: 'w-[450px]',
    height: 'h-[80vh] max-h-[800px]',
    bottom: 'bottom-6',
    right: 'right-6',
    messageHeight: 'h-[65vh]'
  }
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDimensions, setCurrentDimensions] = useState(DIMENSIONS.desktop);
  const [inputMessage, setInputMessage] = useState('');
  const chatRef = useRef(null);
  
  const { 
    messages, 
    isTyping, 
    unreadCount, 
    isMuted, 
    toggleMute, 
    processMessage,
    addMessage 
  } = useChatBot();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCurrentDimensions(DIMENSIONS.mobile);
      } else if (window.innerWidth < 1024) {
        setCurrentDimensions(DIMENSIONS.tablet);
      } else {
        setCurrentDimensions(DIMENSIONS.desktop);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto scroll to bottom when new message arrives
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    try {
      addMessage(inputMessage, true);
      await processMessage(inputMessage);
      setInputMessage('');
      chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
    } catch (error) {
      console.error('Failed to process message:', error);
    }
  };

  return (
    <>
      {/* Chat Button with Logo */}
      <motion.button
          aria-label="Open chat"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r p-1 from-blue-500 to-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <img 
            src="https://res.cloudinary.com/dqovjmmlx/image/upload/v1735390287/fffff_vxggl0.png" 
            alt="Chat Bot" 
            className="w-10 h-10"
          />
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm animate-pulse">
              {unreadCount}
            </span>
          )}
        </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed ${currentDimensions.width} ${currentDimensions.height} 
                       ${currentDimensions.bottom} ${currentDimensions.right} 
                       bg-gray-900 rounded-2xl shadow-2xl z-50 overflow-hidden 
                       border border-gray-800 backdrop-blur-lg`}
          >
            {/* Header with Banner */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    className="relative group"
                  >
                    <img 
                      src="https://res.cloudinary.com/dqovjmmlx/image/upload/v1735390287/fffff_vxggl0.png" 
                      alt="AI Bot" 
                      className="w-10 h-10 rounded-full ring-2 ring-white/50"
                    />
                    <div className="absolute -top-10 left-0 hidden group-hover:block bg-white p-2 rounded-lg shadow-xl">
                      <p className="text-sm text-gray-700">AI Auroco v1.0</p>
                    </div>
                  </motion.div>
                  <div>
                    <h3 className="font-bold text-white text-lg">Auroco</h3>
                    {isTyping && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center space-x-2 text-gray-400"
                      >
                        <div className="flex space-x-1">
                          {[1, 2, 3].map((dot) => (
                            <motion.span
                              key={dot}
                              className="w-2 h-2 bg-blue-400 rounded-full"
                              animate={{
                                y: ["0%", "-50%", "0%"],
                                opacity: [0.4, 1, 0.4]
                              }}
                              transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                delay: dot * 0.2
                              }}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-500">Typing...</span>
                      </motion.div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={toggleMute} 
                    className="text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    {isMuted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
                  </button>
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            <div 
              ref={chatRef}
              className={`${currentDimensions.messageHeight} overflow-y-auto 
                scrollbar-thin scrollbar-thumb-blue-500/50 scrollbar-track-gray-800/50
                px-4 py-4 space-y-6 relative`}
            >
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: message.type === 'user' ? 20 : -20, y: 10 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 200,
                    damping: 20,
                    duration: 0.3 
                  }}
                  className={`flex items-end space-x-2 ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.type === 'bot' && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0"
                    >
                      <img 
                        src="https://res.cloudinary.com/dqovjmmlx/image/upload/v1735390287/fffff_vxggl0.png"
                        alt="Bot"
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  )}

                  <motion.div
                    layout
                    className={`max-w-[85%] p-4 rounded-2xl shadow-lg ${
                      message.type === 'user' 
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white ml-auto' 
                        : 'bg-gray-800/90 backdrop-blur-sm text-gray-100'
                    }`}
                  >
                    <p className="text-sm md:text-base leading-relaxed">{message.content}</p>
                    
                    {message.options && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-wrap gap-2 mt-3"
                      >
                        {message.options.map(option => (
                          <motion.button
                            key={option}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => processMessage(option)}
                            className="px-4 py-2 text-sm bg-gray-700/50 rounded-full
                              hover:bg-blue-500/20 transition-all duration-300
                              border border-gray-600/30 backdrop-blur-sm"
                          >
                            {option}
                          </motion.button>
                        ))}
                      </motion.div>
                    )}

                    <span className="text-xs text-gray-400 mt-2 block">
                      {new Date(message.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </motion.div>

                  {message.type === 'user' && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-6 h-6 rounded-full bg-blue-500 flex-shrink-0 
                        flex items-center justify-center text-xs text-white font-medium"
                    >
                      You
                    </motion.div>
                  )}
                </motion.div>
              ))}

              {/* Typing Indicator with Enhanced Animation */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center space-x-2 text-gray-400"
                >
                  <div className="flex space-x-1">
                    {[1, 2, 3].map((dot) => (
                      <motion.span
                        key={dot}
                        className="w-2 h-2 bg-blue-400 rounded-full"
                        animate={{
                          y: ["0%", "-50%", "0%"],
                          opacity: [0.4, 1, 0.4]
                        }}
                        transition={{
                          duration: 0.6,
                          repeat: Infinity,
                          delay: dot * 0.2
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">Typing...</span>
                </motion.div>
              )}
            </div>

            {/* Input Form */}
            <form 
              onSubmit={handleSubmit}
              className="absolute bottom-0 left-0 right-0 p-4 bg-gray-900/80 backdrop-blur border-t border-gray-800"
            >
              <div className="flex space-x-2">
              <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 
                          placeholder-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      handleSubmit(e);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={isTyping || !inputMessage.trim()}
                  className={` rounded-lg px-4 transition-all duration-300 
                            ${isTyping || !inputMessage.trim() 
                              ? 'bg-gray-700 cursor-not-allowed' 
                              : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {isTyping ? 
                    <FaSpinner className="animate-spin" /> : 
                    <FaPaperPlane className="text-white" />
                  }
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;